/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module UserProfileModule
 */

import {
    Component,
    Inject,
    Input,
    OnInit,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { ClrFormLayout } from '@clr/angular';
import { UserProfileItem } from 'ajs/modules/accounts';
import { SystemConfig } from 'ajs/modules/system/factories/system-config.item.factory';
import * as l10n from './user-profile-modal.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Modal component for User Profile Modal.
 *
 * @author Kondiparthi Shanmukha Sarath
 */
@Component({
    selector: 'user-profile-modal',
    templateUrl: './user-profile-modal.component.html',
})
export class UserProfileModalComponent implements OnInit {
    /**
     * UserProfileItem ObjectTypeItem Instance
     */
    @Input()
    public editable: UserProfileItem;

    /**
     * UserProfileItem Object Type.
     */
    public objectType: string;

    /**
     * Keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Layout for User Profile modal clrForm.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * Flag to check if password policy is managed by VCF Ops.
     */
    private isPasswordPolicyManagedByVcfOps = false;

    constructor(
        private readonly l10nService: L10nService,
        @Inject('systemConfigService')
        private readonly systemConfigService: SystemConfig,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.objectType = this.editable.messageType;
        this.systemConfigService.loadConfig().then(() => {
            const systemConfig = this.systemConfigService.getConfig();

            this.isPasswordPolicyManagedByVcfOps = Boolean(systemConfig?.password_policy_managed_at_ops);

            if (this.isPasswordPolicyManagedByVcfOps) {
                const customAlertMessage = this.l10nService.getMessage(
                    this.l10nKeys.passwordPolicyManagedByVcfOpsWarning,
                );

                this.setCustomAlertMessage([customAlertMessage]);
            }
        });
    }

    /**
     * Set custom alert message to errors and warnings.
     */
    private setCustomAlertMessage(messages: string[] = []): void {
        const { errors = [], warnings = [] } = this.editable;
        const areWarningsArrayOrNull = Array.isArray(warnings) || warnings === null;

        // Make sure errors are not null, as null errors may not render warnings.
        this.editable.errors = errors ?? [];

        // Append custom alert message to warnings if warnings are array or null
        if (areWarningsArrayOrNull) {
            this.editable.warnings = [...warnings ?? [], ...messages];
        }
    }
}
